// Adaptive question engine (aimed at 7–9 year olds, UK Year 3/4 curriculum).
// Every topic has levels; three first-try points in a row level a topic up (a won challenge
// counts double), and a wrong answer nudges it back down. Times-table facts are tracked individually.
import { RNG, escapeHtml } from './util.js';

// `start` is where a brand-new player begins (pitched for age 8); saved levels are never moved.
export const TOPICS = {
  add: { name: 'Adding', icon: '➕', max: 5, start: 3 },
  sub: { name: 'Taking Away', icon: '➖', max: 5, start: 3 },
  mul: { name: 'Times Tables', icon: '✖️', max: 4, start: 2 },
  div: { name: 'Dividing', icon: '➗', max: 4, start: 2 },
  missing: { name: 'Missing Numbers', icon: '🔍', max: 4, start: 2 },
  word: { name: 'Word Problems', icon: '📖', max: 4, start: 2 },
  spell: { name: 'Spelling', icon: '🔤', max: 3, start: 2 },
  time: { name: 'Telling Time', icon: '🕒', max: 5, start: 2 },
  fraction: { name: 'Fractions', icon: '🍕', max: 4, start: 2 },
  pattern: { name: 'Number Patterns', icon: '🔢', max: 4, start: 2 },
  place: { name: 'Place Value', icon: '💯', max: 4, start: 2 },
  shapes: { name: 'Shapes', icon: '🔷', max: 3, start: 1 },
};

// Explore lessons climb a small ladder: a warm-up below your level, one at your level, then a
// 🔥 challenge one level up. Returns the boost for the next question and whether it is the challenge.
export const LESSON_LADDER = [-1, 0, 1];
export function ladderRung(ladder, correct, needed) {
  const i = Math.max(0, Math.min(correct, needed - 1, ladder.length - 1));
  return { boost: ladder[i], challenge: ladder.length > 1 && i === ladder.length - 1 };
}

export const TOPIC_HINTS = {
  add: 'Split the numbers into tens and ones. Add each part, then put them together.',
  sub: 'Try taking away the tens first, then the ones. You can count up to check.',
  mul: 'Count equal groups. You can double for ×2, or count in fives for ×5.',
  div: 'Think of the times table backwards. How many equal groups fit?',
  missing: 'Use the opposite operation. Addition and subtraction undo each other.',
  word: 'What do you know? Are you combining, taking away, making groups or sharing?',
  spell: 'Say the word slowly. Look carefully at the middle and the ending.',
  time: 'The short hand tells the hour. The long hand counts minutes in fives.',
  fraction: 'Count all the equal parts for the bottom number. Count the selected parts for the top.',
  pattern: 'Compare neighbouring numbers. What is being added or taken away each time?',
  place: 'Read the columns: thousands, hundreds, tens, ones. The position changes the value.',
  shapes: 'Count the sides or faces carefully. A corner is where edges meet.',
};

const r = new RNG((Date.now() ^ 0x1234567) >>> 0);
const shuffle = (a) => r.shuffle(a);

function numChoices(ans, cands) {
  const set = new Set([ans]);
  const out = [];
  for (const c of shuffle(cands.slice())) {
    if (out.length >= 3) break;
    if (!Number.isFinite(c) || c < 0 || !Number.isInteger(c) || set.has(c)) continue;
    set.add(c);
    out.push(c);
  }
  let k = 1;
  while (out.length < 3) {
    const c = ans + (k % 2 ? 1 : -1) * Math.ceil(k / 2) * (ans >= 20 ? r.pick([1, 2, 10]) : 1);
    k++;
    if (c >= 0 && !set.has(c)) {
      set.add(c);
      out.push(c);
    }
    if (k > 40) break;
  }
  return shuffle([ans, ...out]).map(String);
}

function strChoices(ans, wrongs) {
  const set = new Set([ans]);
  const out = [];
  for (const w of shuffle(wrongs.slice())) {
    if (out.length >= 3) break;
    if (!set.has(w)) {
      set.add(w);
      out.push(w);
    }
  }
  return shuffle([ans, ...out]);
}

const frac = (n, d) => `<span class="frac"><span>${n}</span><span>${d}</span></span>`;
const box = '<span class="qbox">?</span>';
const skip = (t, n) => Array.from({ length: n }, (_, i) => (i + 1) * t).join(', ');
// Top-level times tables and division: the tricky tables from 6 to 12. A grown-up's focus in Settings still wins.
const TOP_TABLES = [6, 7, 8, 9, 11, 12];
function topTables(ctx) {
  if (!ctx.focus) return TOP_TABLES;
  const big = ctx.tables.filter((t) => t >= 6);
  return big.length ? big : ctx.tables;
}
const FRACTION_NAMES = { 3: 'third', 4: 'quarter', 5: 'fifth', 6: 'sixth', 7: 'seventh', 8: 'eighth', 9: 'ninth', 10: 'tenth', 12: 'twelfth' };
const fracWords = (n, d) => `${n} ${FRACTION_NAMES[d]}${n === 1 ? '' : 's'}`;
const plain = (html) => html.replace(/<[^>]+>/g, '');

const TABLE_TIPS = {
  2: 'Times 2 means <b>double it</b>!',
  3: 'Count up in 3s: 3, 6, 9, 12, 15…',
  4: 'Times 4: <b>double, then double again</b>!',
  5: 'Times 5 answers always end in <b>0 or 5</b>.',
  6: 'Times 6: do times 3, then double it!',
  7: 'Tricky 7s! Remember: <b>7 × 8 = 56</b> (5, 6, 7, 8!).',
  8: 'Times 8: <b>double, double, double</b>!',
  9: 'Times 9: the tens go <b>up</b> and the ones go <b>down</b>: 9, 18, 27, 36…',
  10: 'Times 10: <b>add a zero</b> on the end!',
  11: 'Times 11 (up to 9): <b>write the number twice</b> — 11 × 4 = 44!',
  12: 'Times 12: do times 10, then add times 2!',
};

// ---------- word lists for spelling ----------
const SPELL = {
  1: `because|becuase|becoz|beacause people|peple|poeple|peeple friend|freind|frend|frende school|skool|shcool|scool would|wuld|whould|woud could|cood|culd|coud should|shoud|shuld|shood beautiful|beutiful|beautifull|bootiful children|childern|chilldren|chidren every|evry|evrey|everie again|agen|agian|agane money|mony|munny|monie pretty|prity|pritty|prettie water|warter|wotter|watter after|arfter|aftur|affter busy|bizzy|buisy|bisy great|grait|graet|greight climb|clime|clim|climbe clothes|cloths|clotes|clothse half|harf|haff|halph door|dore|dor|doar floor|flore|flor|floar sugar|shugar|suger|sugur kind|kined|kynd|kinde behind|behynd|behined|beehind most|moast|moste|mosst cold|coald|colde|kold even|eeven|evun|evan class|clas|clarss|klass hour|howr|houre|ower`,
  2: `believe|beleive|belive|beleave answer|anser|answere|arnswer appear|apear|appeer|appere arrive|arive|arrivve|arrieve bicycle|bicicle|bycycle|bisycle build|bild|biuld|buld caught|cought|cort|caugth certain|certin|sertain|certan circle|cirkle|sircle|circel decide|deside|decied|desside different|diffrent|diferent|differant difficult|dificult|difficalt|diffacult disappear|dissapear|disapear|disappeer early|erly|earley|urly earth|erth|eath|urth eight|eigth|aight|eiht enough|enuff|enouf|enogh exercise|exersize|excercise|exercize famous|famus|faimous|famouse February|Febuary|Feburary|Febrary forward|foward|forwerd|forword fruit|froot|frute|friut group|groop|grup|groupe guard|gaurd|gard|guarde guide|gide|guied|giude heard|herd|heared|hurd heart|hart|haert|hearte height|hight|heigth|hieght history|histery|histroy|histry imagine|imagin|imajine|immagine island|iland|islend|eyeland knowledge|knowlege|nolledge|knowladge learn|lern|leran|lurn library|libary|libery|liberry minute|minit|minnute|minuet naughty|norty|nauhgty|naughtey notice|notise|notis|notiec often|offen|ofen|oftin opposite|oposite|opposit|oppisite popular|populer|poppular|populare possible|posible|possable|possibel promise|promis|promiss|prommise quarter|quater|qwarter|quartor question|questoin|qestion|quesion remember|remeber|rember|remembar sentence|sentance|sentense|sentince special|speshal|specal|spetial straight|strait|staight|straigt strange|strainge|strang|strenge surprise|suprise|surprize|serprise thought|thort|thougt|thout through|threw|thru|throgh weight|wieght|weigth|wayt women|wimmin|womin|wommen`,
  3: `accident|accidant|acident|accsident actually|actualy|akshually|actully business|buisness|busness|bizness calendar|calender|calandar|callendar complete|compleet|compleat|complette consider|concider|consder|considder continue|contiue|continu|contineu describe|discribe|describ|descibe experience|experiance|expirience|experence experiment|expirement|experimant|experament extreme|extreem|extream|extrem grammar|grammer|gramar|grammur important|importent|importint|impotant increase|increse|encrease|increace interest|intrest|interist|intrust material|materiel|matirial|materal medicine|medisine|medecine|medcine mention|menshun|mension|mentoin natural|nateral|nacheral|naturel occasion|ocasion|occassion|occation ordinary|ordinery|ordenary|ordinairy particular|perticular|particuler|particlar peculiar|peculier|pecular|peculiur perhaps|perhap|praps|perhapps position|posision|possition|posishun pressure|presure|pressur|preshure probably|probly|probbably|probabley recent|resent|recint|recant regular|reguler|regullar|reglar separate|seperate|separete|seperat suppose|supose|suppoze|soppose therefore|therfore|therefor|thearfore various|varius|vairous|verious`,
};
const SPELL_LISTS = {};
for (const [lvl, s] of Object.entries(SPELL)) SPELL_LISTS[lvl] = s.trim().split(/\s+/).map((e) => e.split('|'));

const SPELL_TIPS = {
  believe: 'Remember: <b>i before e</b> — be-<b>lie</b>-ve. Don’t believe a lie!',
  separate: 'There’s <b>a rat</b> in sep-<b>a rat</b>-e!',
  island: 'Island has a <b>silent s</b>: i<b>s</b>-land.',
  February: 'Say it slowly: Feb-<b>ru</b>-ar-y.',
  because: 'Big Elephants Can Always Understand Small Elephants — <b>B-E-C-A-U-S-E</b>!',
  friend: 'A <b>fri</b>end is there to the <b>end</b>!',
  knowledge: 'It starts with a silent <b>k</b> — like <b>know</b>!',
  height: 'Height has <b>eight</b> hiding inside it!',
  weight: 'Weight has <b>eight</b> inside it too!',
  heart: 'You can <b>hear</b> your <b>heart</b>!',
  library: 'Say it clearly: li-<b>brar</b>-y.',
  climb: 'Climb has a <b>silent b</b> at the end.',
  hour: 'Hour has a <b>silent h</b>.',
  guard: 'A <b>gu</b>ard — the u is silent but it’s there!',
  guide: 'A <b>gu</b>ide — the u is silent but it’s there!',
  necessary: 'One collar, two sleeves!',
  answer: 'Answer has a <b>silent w</b>: ans-<b>w</b>-er.',
  surprise: 'Remember the first <b>r</b>: su<b>r</b>-prise.',
  disappear: 'dis + appear = <b>disappear</b> (one s, two p’s).',
  business: '<b>Bus</b>-i-ness — you can catch a bus to work!',
  calendar: 'Calend<b>ar</b> ends in <b>ar</b>.',
  grammar: 'Don’t mar your gram<b>mar</b>!',
};

// ---------- word problems ----------
const WORD_ADD = [
  '{name} mined {a} diamonds in the morning and {b} more in the afternoon. How many diamonds did {name} mine?',
  'There are {a} slimes on one island and {b} slimes on another. How many slimes are there altogether?',
  '{name} built a tower {a} blocks tall, then added {b} more blocks. How tall is the tower now?',
  '{name} jumped over {a} lava pits, then {b} more. How many lava pits did {name} jump over?',
  '{name} collected {a} coins in World 1 and {b} coins in World 2. How many coins is that in total?',
  'A villager has {a} apples and {name} gives him {b} more. How many apples does the villager have now?',
];
const WORD_SUB = [
  '{name} had {a} coins and spent {b} on a new sword. How many coins are left?',
  'A Zombie Noob had {a} cookies. {name} took {b} of them. How many cookies does the zombie have now?',
  'The bridge had {a} blocks. The Glitch King smashed {b} of them. How many blocks are left?',
  '{name} needs {a} blocks to build a house and has {b}. How many more blocks does {name} need?',
  'There were {a} Boom Bots. {name} bonked {b} of them. How many Boom Bots are left?',
  '{name}’s tower was {a} blocks tall. A Boom Bot blew up {b} blocks. How tall is it now?',
];
const WORD_MUL = [
  'There are {a} chests. Each chest holds {b} gems. How many gems are there altogether?',
  '{name} builds {a} towers. Each tower is {b} blocks tall. How many blocks did {name} use?',
  '{a} Boom Bots each have 4 legs. How many legs is that altogether?',
  'Each slime splits into {b} mini slimes. If {a} slimes split, how many mini slimes are there?',
  '{name} plants {a} rows of pumpkins with {b} pumpkins in each row. How many pumpkins are there?',
  'A potion costs {b} coins. How many coins do {a} potions cost?',
];
const WORD_DIV = [
  '{name} shares {p} cookies equally between {b} villagers. How many cookies does each villager get?',
  '{name} stacks {p} blocks into towers of {b}. How many towers can {name} build?',
  '{p} gems are shared equally into {b} chests. How many gems go in each chest?',
  'A team of {b} players found {p} diamonds and shared them equally. How many did each player get?',
];

const NUM_WORDS = { 5: 'five', 10: 'ten', 20: 'twenty', 25: 'twenty-five' };
function timeWords(h, m) {
  const next = (h % 12) + 1;
  if (m === 0) return `${h} o’clock`;
  if (m === 30) return `half past ${h}`;
  if (m === 15) return `quarter past ${h}`;
  if (m === 45) return `quarter to ${next}`;
  if (m < 30) return `${NUM_WORDS[m] || m} past ${h}`;
  return `${NUM_WORDS[60 - m] || 60 - m} to ${next}`;
}
const digital = (h, m) => `${h}:${m.toString().padStart(2, '0')}`;

const SHAPES2D = [
  ['triangle', 3], ['square', 4], ['rectangle', 4], ['pentagon', 5], ['hexagon', 6], ['octagon', 8],
];
const SHAPES3D = [
  ['How many faces does a cube have? (Like a Minecraft block!)', 6, 'cube'],
  ['How many corners (vertices) does a cube have?', 8, 'cube'],
  ['How many edges does a cube have?', 12, 'cube'],
  ['How many faces does a square-based pyramid have?', 5, 'pyramid'],
  ['How many faces does a cuboid have?', 6, 'cuboid'],
  ['How many corners does a triangle have?', 3, 'triangle'],
];
// Top level: right angles in shapes and turns, then faces, edges and vertices of prisms and pyramids.
// [question, answer, picture, explanation]
const RIGHT_ANGLES = [
  ['How many right angles does a <b>square</b> have?', 4, { kind: 'shape', name: 'square', sides: 4 }, 'Every corner of a square is a right angle, like the corner of a page: <b>4</b>.'],
  ['How many right angles does a <b>rectangle</b> have?', 4, { kind: 'shape', name: 'rectangle', sides: 4 }, 'A rectangle has a right angle in each corner: <b>4</b>.'],
  ['How many right angles does this triangle have?', 1, { kind: 'shape', name: 'right-angled triangle', sides: 3 }, 'It is a right-angled triangle, with <b>1</b> square corner. A triangle can never have two!'],
  ['How many right angles does a regular <b>hexagon</b> have?', 0, { kind: 'shape', name: 'hexagon', sides: 6 }, 'Every corner of a regular hexagon is wider than a right angle, so it has <b>0</b>.'],
  ['How many right angles make a <b>half turn</b>?', 2, null, 'A quarter turn is 1 right angle, so a half turn is <b>2</b>.'],
  ['How many right angles make a <b>three-quarter turn</b>?', 3, null, 'Each quarter turn is a right angle, so three quarter turns make <b>3</b>.'],
  ['How many right angles make a <b>full turn</b>?', 4, null, 'Four quarter turns take you all the way round: <b>4</b> right angles.'],
];
// Faces, edges and vertices of each solid; the questions use the others as tempting wrong answers.
const SOLIDS = { prism: [5, 9, 6], pyramid: [5, 8, 5], tetra: [4, 6, 4], cuboid: [6, 12, 8] };
const SHAPES3D_TOP = [
  ['How many faces does a triangular prism have?', 5, 'prism', 'A triangular prism has <b>5</b> faces: 2 triangles and 3 rectangles.'],
  ['How many edges does a triangular prism have?', 9, 'prism', 'A triangular prism has <b>9</b> edges: 3 round each triangle and 3 joining them.'],
  ['How many vertices (corners) does a triangular prism have?', 6, 'prism', 'A triangular prism has <b>6</b> vertices: 3 on each triangle end.'],
  ['How many edges does a square-based pyramid have?', 8, 'pyramid', 'A square-based pyramid has <b>8</b> edges: 4 round the square and 4 up to the point.'],
  ['How many vertices (corners) does a square-based pyramid have?', 5, 'pyramid', 'A square-based pyramid has <b>5</b> vertices: 4 round the square and 1 at the top.'],
  ['How many faces does a triangular-based pyramid have?', 4, 'tetra', 'A triangular-based pyramid has <b>4</b> faces, and every face is a triangle.'],
  ['How many edges does a triangular-based pyramid have?', 6, 'tetra', 'A triangular-based pyramid has <b>6</b> edges: 3 round the base and 3 up to the point.'],
  ['How many edges does a cuboid have?', 12, 'cuboid', 'A cuboid has <b>12</b> edges: 4 on top, 4 underneath and 4 going up.'],
];

export class QuestionEngine {
  constructor(saveRef) {
    this.saveRef = saveRef; // function returning save data
    this.recent = [];
    this.recentWords = [];
  }
  get save() {
    return this.saveRef();
  }

  topicState(t) {
    const s = this.save;
    s.topics = s.topics || {};
    if (!s.topics[t]) s.topics[t] = { lvl: TOPICS[t].start, n: 0, c: 0, streak: 0 };
    return s.topics[t];
  }

  level(t, boost = 0) {
    const st = this.topicState(t);
    const diff = this.save.settings?.difficulty || 'auto';
    const shift = diff === 'easy' ? -1 : diff === 'hard' ? 1 : 0;
    const v = Math.max(1, Math.min(TOPICS[t].max, st.lvl + shift + boost));
    const base = Math.floor(v);
    const L = base + (r.next() < v - base ? 1 : 0);
    return Math.max(1, Math.min(TOPICS[t].max, L));
  }

  enabledTopics(weights) {
    const on = this.save.settings?.topics || {};
    const out = [];
    for (const [t, w] of Object.entries(weights)) if (on[t] !== false && TOPICS[t]) out.push([t, w]);
    if (!out.length) {
      for (const t of Object.keys(TOPICS)) if (on[t] !== false) out.push([t, 1]);
    }
    if (!out.length) out.push(['add', 1], ['mul', 1]);
    return out;
  }

  // The tables a grown-up chose in Settings, or null when every table is on.
  focusTables() {
    const focus = this.save.settings?.tables?.filter(t => Number.isInteger(t) && t >= 2 && t <= 12);
    return focus && focus.length && focus.length < 11 ? focus.slice() : null;
  }

  tables(worldTables) {
    return this.focusTables() || (worldTables && worldTables.length ? worldTables.slice() : [2, 3, 4, 5, 10]);
  }

  // opts: {weights, tables, topic, numeric, boost}. Explore lessons pass a ladder boost per question.
  next(opts = {}) {
    let topic = opts.topic;
    if (!topic) {
      const list = this.enabledTopics(opts.weights || { add: 1, sub: 1, mul: 2 });
      // don't repeat the same topic 3 times in a row
      const last2 = this.recent.slice(-2).map((q) => q.topic);
      const filtered = list.filter(([t]) => !(last2.length === 2 && last2[0] === t && last2[1] === t));
      topic = r.weighted(filtered.length ? filtered : list);
    }
    if (opts.numeric && ['spell', 'time', 'shapes'].includes(topic)) topic = 'mul';
    // level() keeps every boost (Adventure's small bump or a lesson's ladder rung) inside the topic's levels.
    const L = this.level(topic, opts.boost || 0);
    const ctx = { tables: this.tables(opts.tables), focus: !!this.focusTables(), numeric: opts.numeric };
    let q = null;
    for (let tries = 0; tries < 8 && !q; tries++) {
      q = GEN[topic].call(this, L, ctx);
      if (q && this.recent.some((p) => p.key === q.key) && tries < 6) q = null;
      if (q && opts.numeric && (!q.numeric || q.visual)) q = null;
    }
    if (!q) {
      topic = opts.numeric ? 'mul' : 'add';
      q = GEN[topic].call(this, Math.min(2, TOPICS[topic].max), ctx);
    }
    q.hint = q.hint || TOPIC_HINTS[topic];
    q.topic = topic;
    q.level = L;
    q.key = q.key || q.text;
    this.recent.push(q);
    if (this.recent.length > 10) this.recent.shift();
    return q;
  }

  // returns true if this topic levelled up
  record(q, correct, firstTry) {
    const st = this.topicState(q.topic);
    // Saves from before 1.7 have no `best`: start it from the level held before this answer can lower it.
    if (!(st.best >= st.lvl)) st.best = st.lvl;
    st.n++;
    let up = false;
    if (correct) st.c++;
    if (correct && firstTry) {
      // A challenge won on the first try is worth two of the three points that level a topic up.
      st.streak = (st.streak || 0) + (q.challenge ? 2 : 1);
      if (st.streak >= 3 && st.lvl < TOPICS[q.topic].max) {
        st.lvl = Math.min(TOPICS[q.topic].max, Math.floor(st.lvl) + 1);
        st.streak = 0;
        up = true;
      }
    } else {
      st.streak = 0;
      // A challenge is asked one level up, so missing it never lowers the level.
      if (!correct && !q.challenge) st.lvl = Math.max(1, st.lvl - 0.5);
    }
    // The highest level ever reached, so top-level rewards stay earned after a later slip.
    if (!(st.best >= st.lvl)) st.best = st.lvl;
    if (q.factKey) {
      const s = this.save;
      s.facts = s.facts || {};
      const f = (s.facts[q.factKey] = s.facts[q.factKey] || { n: 0, c: 0, s: 0, t: 0 });
      f.n++;
      if (correct && firstTry) {
        f.c++;
        f.s = Math.min(9, f.s + 1);
      } else if (!correct) f.s = 0;
      f.t = Date.now();
    }
    return up;
  }

  factMastery(a, b) {
    const k = `${Math.min(a, b)}x${Math.max(a, b)}`;
    const f = this.save.facts?.[k];
    if (!f) return 0; // unseen
    if (f.s >= 3 && f.c / f.n >= 0.7) return 3; // mastered
    if (f.s >= 1) return 2; // learning well
    return 1; // seen / needs practice
  }

  tableMastered(t) {
    for (let n = 1; n <= 12; n++) if (this.factMastery(t, n) < 3) return false;
    return true;
  }

  // `skip`: a multiplier left out (top levels skip the easy ×10).
  pickFact(tables, nMin, nMax, skip = null) {
    const facts = this.save.facts || {};
    const recentKeys = new Set(this.recent.slice(-6).map((q) => q.factKey).filter(Boolean));
    const cands = [];
    for (const t of tables)
      for (let n = nMin; n <= nMax; n++) {
        if (n === skip) continue;
        const key = `${Math.min(t, n)}x${Math.max(t, n)}`;
        const f = facts[key];
        let w = f ? 1 + (5 - Math.min(5, f.s)) * 1.5 : 4;
        if (f && f.n > 0 && f.c / f.n < 0.6) w += 3;
        if (recentKeys.has(key)) w *= 0.12;
        if (n === 1 || t === 1) w *= 0.35;
        cands.push([[t, n], w]);
      }
    return r.weighted(cands);
  }
}

const GEN = {
  add(L) {
    let a, b;
    if (L === 1) {
      a = r.int(1, 8);
      b = r.int(1, 10 - a);
    } else if (L === 2) {
      a = r.int(4, 12);
      b = r.int(3, 9);
      if (a + b > 20) b = 20 - a;
    } else if (L === 3) {
      a = r.int(12, 88);
      b = r.int(3, 9);
    } else if (L === 4) {
      a = r.int(14, 67);
      b = r.int(12, Math.max(12, 98 - a));
    } else {
      a = r.int(110, 580);
      b = r.int(35, 390);
    }
    const ans = a + b;
    let explain;
    if (L <= 2) {
      if (a < 10 && ans > 10) explain = `Make 10! ${a} + ${10 - a} = 10, then add ${b - (10 - a)} more → <b>${ans}</b>.`;
      else explain = `Start at <b>${Math.max(a, b)}</b> and count on ${Math.min(a, b)}: ${Array.from({ length: Math.min(a, b) }, (_, i) => Math.max(a, b) + i + 1).join(', ')}.`;
    } else if (L === 3) {
      const toTen = 10 - (a % 10);
      explain = toTen < b && toTen !== 10 ? `${a} + ${toTen} = ${a + toTen}, then ${b - toTen} more = <b>${ans}</b>.` : `${a} + ${b} = <b>${ans}</b>. Add the ones: ${a % 10} + ${b} = ${(a % 10) + b}.`;
    } else if (L === 4) {
      const t = Math.floor(a / 10) * 10 + Math.floor(b / 10) * 10, o = (a % 10) + (b % 10);
      explain = `Tens: ${Math.floor(a / 10) * 10} + ${Math.floor(b / 10) * 10} = ${t}. Ones: ${a % 10} + ${b % 10} = ${o}. Then ${t} + ${o} = <b>${ans}</b>.`;
    } else {
      const h = Math.floor(a / 100) * 100 + Math.floor(b / 100) * 100;
      const t = (Math.floor(a / 10) % 10) * 10 + (Math.floor(b / 10) % 10) * 10;
      const o = (a % 10) + (b % 10);
      explain = `Hundreds: ${h}. Tens: ${t}. Ones: ${o}. Total: ${h} + ${t} + ${o} = <b>${ans}</b>.`;
    }
    const noCarry = Number(String(a).padStart(3, '0').split('').map((d, i) => (Number(d) + Number(String(b).padStart(3, '0')[i])) % 10).join(''));
    return { text: `${a} + ${b} = ?`, prompt: 'Add them up!', answer: String(ans), choices: numChoices(ans, [ans + 1, ans - 1, ans + 10, ans - 10, noCarry, ans + 2]), explain, key: `add${a}+${b}`, numeric: true };
  },
  sub(L) {
    let a, b;
    if (L === 1) {
      a = r.int(4, 10);
      b = r.int(1, a - 1);
    } else if (L === 2) {
      a = r.int(11, 20);
      b = r.int(2, 9);
    } else if (L === 3) {
      a = r.int(21, 99);
      b = r.int(3, 9);
    } else if (L === 4) {
      a = r.int(40, 99);
      b = r.int(11, a - 10);
    } else {
      a = r.int(210, 990);
      b = r.int(45, a - 100);
    }
    const ans = a - b;
    let explain;
    if (L <= 2) explain = `Count back ${b} from ${a}: ${Array.from({ length: b }, (_, i) => a - i - 1).join(', ')}. So ${a} − ${b} = <b>${ans}</b>.`;
    else if (L === 3) {
      const o = a % 10;
      explain = b > o && o > 0 ? `${a} − ${o} = ${a - o}, then take away ${b - o} more = <b>${ans}</b>.` : `${a} − ${b} = <b>${ans}</b>.`;
    } else {
      const tens = Math.floor(b / 10) * 10, ones = b % 10;
      explain = `${a} − ${tens} = ${a - tens}, then ${a - tens} − ${ones} = <b>${ans}</b>.`;
    }
    const swapErr = Number(String(a).split('').map((d, i, arr) => Math.abs(Number(d) - Number(String(b).padStart(arr.length, '0')[i]))).join(''));
    return { text: `${a} − ${b} = ?`, prompt: 'Take it away!', answer: String(ans), choices: numChoices(ans, [ans + 1, ans - 1, ans + 10, ans - 10, swapErr, ans + 2]), explain, key: `sub${a}-${b}`, numeric: true };
  },
  mul(L, ctx) {
    // Top level: the tricky Year 4 facts, both numbers 6–12 (inside a grown-up's chosen tables).
    const nMax = L === 1 ? 5 : L === 2 ? 10 : 12;
    const nMin = L === 1 ? 1 : L >= 4 ? 6 : 2;
    const [t, n] = L >= 4 ? this.pickFact(topTables(ctx), nMin, nMax, 10) : this.pickFact(ctx.tables, nMin, nMax);
    const ans = t * n;
    const flip = r.chance(0.5);
    const text = flip ? `${n} × ${t} = ?` : `${t} × ${n} = ?`;
    const explain = `<b>${t} × ${n} = ${ans}</b><br>Count in ${t}s: ${skip(t, n)}.<br><span class="tip">💡 ${TABLE_TIPS[t] || ''}</span>`;
    const digitSwap = ans >= 10 ? Number(String(ans).split('').reverse().join('')) : ans + 3;
    return {
      text, prompt: `The ${t} times table`, answer: String(ans),
      choices: numChoices(ans, [t * (n + 1), t * (n - 1), (t + 1) * n, (t - 1) * n, digitSwap, ans + 1, ans - 1]),
      explain, factKey: `${Math.min(t, n)}x${Math.max(t, n)}`, key: `mul${t}x${n}`, numeric: true, fact: [t, n],
    };
  },
  div(L, ctx) {
    // Top level: dividing by 6 or more.
    const nMax = L === 1 ? 5 : L === 2 ? 10 : 12;
    const [t, n] = L >= 4 ? this.pickFact(topTables(ctx), 4, 12, 10) : this.pickFact(ctx.tables.filter((x) => x !== 1), 2, nMax);
    const p = t * n;
    return {
      text: `${p} ÷ ${t} = ?`, prompt: 'Share it out!', answer: String(n),
      choices: numChoices(n, [n + 1, n - 1, n + 2, t, n - 2]),
      explain: `<b>${p} ÷ ${t} = ${n}</b><br>Because ${n} × ${t} = ${p}. Use your times tables backwards!`,
      key: `div${p}/${t}`, numeric: true, factKey: `${Math.min(t, n)}x${Math.max(t, n)}`, fact: [t, n],
    };
  },
  missing(L, ctx) {
    if (L === 1) {
      const s = r.int(8, 20), a = r.int(2, s - 2), b = s - a;
      const first = r.chance(0.5);
      return {
        text: first ? `${box} + ${b} = ${s}` : `${a} + ${box} = ${s}`, speakText: first ? `What plus ${b} equals ${s}?` : `${a} plus what equals ${s}?`, prompt: 'Find the missing number',
        answer: String(first ? a : b), choices: numChoices(first ? a : b, [s, (first ? a : b) + 1, (first ? a : b) - 1, s + b]),
        explain: `Take away to find it: ${s} − ${first ? b : a} = <b>${first ? a : b}</b>.`, numeric: true, key: `m${a}+${b}`,
      };
    }
    if (L === 2) {
      const a = r.int(12, 40), b = r.int(3, 11), c = a - b;
      const first = r.chance(0.5);
      return {
        text: first ? `${box} − ${b} = ${c}` : `${a} − ${box} = ${c}`, speakText: first ? `What minus ${b} equals ${c}?` : `${a} minus what equals ${c}?`, prompt: 'Find the missing number',
        answer: String(first ? a : b), choices: numChoices(first ? a : b, [c + 1, (first ? a : b) + 1, (first ? a : b) - 1, first ? c - b : a + c]),
        explain: first ? `Add back: ${c} + ${b} = <b>${a}</b>.` : `${a} − ${c} = <b>${b}</b>.`, numeric: true, key: `m${a}-${b}`,
      };
    }
    const [t, n] = this.pickFact(ctx.tables, 2, L === 3 ? 10 : 12);
    const p = t * n;
    if (L === 3) {
      return {
        text: r.chance(0.5) ? `${box} × ${t} = ${p}` : `${t} × ${box} = ${p}`, speakText: `What times ${t} equals ${p}?`, prompt: 'Find the missing number',
        answer: String(n), choices: numChoices(n, [n + 1, n - 1, t, n + 2]),
        explain: `${p} ÷ ${t} = <b>${n}</b>, because ${n} × ${t} = ${p}.`, numeric: true, key: `m${t}x${n}`, factKey: `${Math.min(t, n)}x${Math.max(t, n)}`,
      };
    }
    return {
      text: `${box} ÷ ${t} = ${n}`, speakText: `What divided by ${t} equals ${n}?`, prompt: 'Find the missing number',
      answer: String(p), choices: numChoices(p, [p + t, p - t, n + t, p + 1]),
      explain: `Multiply back: ${n} × ${t} = <b>${p}</b>.`, numeric: true, key: `m${p}/${t}`, factKey: `${Math.min(t, n)}x${Math.max(t, n)}`,
    };
  },
  word(L, ctx) {
    const name = escapeHtml(this.save.name);
    const fill = (s, v) => s.replace(/\{(\w+)\}/g, (_, k) => v[k]);
    if (L <= 2) {
      const isAdd = r.chance(0.5);
      let a, b;
      if (L === 1) {
        a = r.int(4, 12);
        b = r.int(2, 8);
      } else {
        a = r.int(15, 60);
        b = r.int(8, 35);
      }
      if (!isAdd && b >= a) [a, b] = [b + a, b];
      const ans = isAdd ? a + b : a - b;
      const tpl = r.pick(isAdd ? WORD_ADD : WORD_SUB);
      return {
        text: fill(tpl, { a, b, name }), word: true, prompt: 'Word problem', answer: String(ans),
        choices: numChoices(ans, [isAdd ? a - b : a + b, ans + 1, ans - 1, ans + 10]),
        explain: `${a} ${isAdd ? '+' : '−'} ${b} = <b>${ans}</b>`, numeric: true, key: tpl + a + b,
      };
    }
    if (L === 3) {
      const isMul = r.chance(0.55);
      const [t, n] = this.pickFact(ctx.tables, 2, 10);
      if (isMul) {
        let tpl = r.pick(WORD_MUL);
        let a = n, b = t;
        if (tpl.includes('4 legs')) b = 4;
        const ans = a * b;
        return {
          text: fill(tpl, { a, b, name }), word: true, prompt: 'Word problem', answer: String(ans),
          choices: numChoices(ans, [a + b, ans + b, ans - b, ans + 1]),
          explain: `${a} groups of ${b}: ${a} × ${b} = <b>${ans}</b>`, numeric: true, key: tpl + a + b,
        };
      }
      const tpl = r.pick(WORD_DIV);
      const p = t * n;
      return {
        text: fill(tpl, { p, b: t, name }), word: true, prompt: 'Word problem', answer: String(n),
        choices: numChoices(n, [n + 1, n - 1, t, p - t]),
        explain: `${p} ÷ ${t} = <b>${n}</b>, because ${n} × ${t} = ${p}.`, numeric: true, key: tpl + p + t,
      };
    }
    // two-step
    const kind = r.int(0, 2);
    if (kind === 0) {
      const a = r.int(5, 30), [t, n] = this.pickFact(ctx.tables, 2, 6);
      const ans = a + t * n;
      return {
        text: `${name} has ${a} coins. ${name} opens ${n} chests with ${t} coins in each. How many coins does ${name} have now?`, word: true, prompt: 'Two-step problem!', answer: String(ans),
        choices: numChoices(ans, [a + t + n, t * n, ans + t, ans - t]),
        explain: `First ${n} × ${t} = ${t * n}. Then ${a} + ${t * n} = <b>${ans}</b>.`, numeric: true, key: `ts${a}${t}${n}`,
      };
    }
    if (kind === 1) {
      const a = r.int(30, 70), b = r.int(5, 20), c = r.int(4, 25);
      const ans = a - b + c;
      return {
        text: `${name} had ${a} blocks. ${name} used ${b} to build a wall, then found ${c} more. How many blocks does ${name} have now?`, word: true, prompt: 'Two-step problem!', answer: String(ans),
        choices: numChoices(ans, [a - b - c, a + b + c, a - b, ans + 10]),
        explain: `${a} − ${b} = ${a - b}. Then ${a - b} + ${c} = <b>${ans}</b>.`, numeric: true, key: `ts2${a}${b}${c}`,
      };
    }
    const [t, n] = this.pickFact(ctx.tables, 2, 8);
    const extra = r.int(2, Math.max(2, Math.min(9, t * n - 3)));
    const ans = t * n - extra;
    return {
      text: `There are ${n} rows of ${t} pumpkins. Zombies squash ${extra} of them. How many pumpkins are left?`, word: true, prompt: 'Two-step problem!', answer: String(ans),
      choices: numChoices(ans, [t * n, t * n + extra, ans - 1, ans + t]),
      explain: `${n} × ${t} = ${t * n}. Then ${t * n} − ${extra} = <b>${ans}</b>.`, numeric: true, key: `ts3${t}${n}${extra}`,
    };
  },
  spell(L) {
    const list = SPELL_LISTS[Math.min(3, L)];
    let entry;
    for (let i = 0; i < 10; i++) {
      entry = r.pick(list);
      if (!this.recentWords.includes(entry[0])) break;
    }
    this.recentWords.push(entry[0]);
    if (this.recentWords.length > 20) this.recentWords.shift();
    const [word, ...wrong] = entry;
    return {
      text: 'Which spelling is correct?', prompt: 'Spelling', spell: word, speakText: `Spell the word: ${word}. ${word}.`,
      answer: word, choices: strChoices(word, wrong),
      explain: `The correct spelling is <b class="big">${word}</b>.${SPELL_TIPS[word] ? `<br><span class="tip">💡 ${SPELL_TIPS[word]}</span>` : ''}`,
      key: 'sp' + word,
    };
  },
  time(L) {
    const h = r.int(1, 12);
    let m;
    if (L === 1) m = 0;
    else if (L === 2) m = r.pick([0, 30, 30]);
    else if (L === 3) m = r.pick([15, 45, 30, 15, 45]);
    else m = r.pick([5, 10, 20, 25, 35, 40, 50, 55, 15, 45]);
    const useDigital = L >= 5;
    const fmt = useDigital ? (hh, mm) => digital(hh, mm) : timeWords;
    const ans = fmt(h, m);
    const wrongs = [];
    const hn = (h % 12) + 1, hp = ((h + 10) % 12) + 1;
    wrongs.push(fmt(hn, m), fmt(hp, m));
    const swapH = m === 0 ? 12 : m / 5;
    const swapM = (h % 12) * 5;
    if (Number.isInteger(swapH) && swapH >= 1 && swapH <= 12) wrongs.push(fmt(swapH, swapM));
    if (m === 45) wrongs.push(useDigital ? digital(h, 15) : `quarter past ${h}`, useDigital ? digital(hn, 45) : `quarter to ${h}`);
    if (m === 15) wrongs.push(useDigital ? digital(h, 45) : `quarter to ${h}`);
    if (m === 30) wrongs.push(useDigital ? digital(h, 0) : `half past ${hn}`);
    if (m === 0) wrongs.push(fmt(h, 30), fmt(12, h * 5 === 60 ? 0 : h * 5));
    wrongs.push(fmt(h, (m + 30) % 60), fmt(h, (m + 5) % 60));
    const tip = m === 0 ? 'When the long hand points to <b>12</b>, it’s o’clock!' : m === 30 ? 'Long hand on <b>6</b> means <b>half past</b>.' : m === 15 ? 'Long hand on <b>3</b> means <b>quarter past</b>.' : m === 45 ? 'Long hand on <b>9</b> means <b>quarter to</b> the next hour.' : 'Each number on the clock is <b>5 minutes</b> for the long hand.';
    return {
      text: 'What time is it?', prompt: 'Telling time', visual: { kind: 'clock', h, m }, answer: ans,
      choices: strChoices(ans, wrongs.filter((w) => w !== ans)),
      explain: `It’s <b>${ans}</b>.<br><span class="tip">💡 The short hand shows the hour. ${tip}</span>`, key: `t${h}:${m}`,
    };
  },
  fraction(L, ctx) {
    if (L >= 4) return fractionTop(ctx);
    if (L === 1) {
      if (r.chance(0.5)) {
        const n = r.int(2, 12) * 2;
        return { text: `What is half of ${n}?`, prompt: 'Fractions', answer: String(n / 2), choices: numChoices(n / 2, [n, n / 2 + 1, n / 2 - 1, n * 2]), explain: `Half means <b>2 equal parts</b>: ${n} ÷ 2 = <b>${n / 2}</b>.`, numeric: true, key: `half${n}` };
      }
      const parts = r.pick([2, 3, 4]);
      return {
        text: 'What fraction is shaded?', prompt: 'Fractions', visual: { kind: 'fraction', parts, shaded: 1, shape: r.pick(['bar', 'circle']) }, answer: `1/${parts}`,
        choices: strChoices(`1/${parts}`, ['1/2', '1/3', '1/4', '2/3', '3/4', `${parts}/1`].filter((x) => x !== `1/${parts}`)), fracChoices: true,
        explain: `There are <b>${parts}</b> equal parts and <b>1</b> is shaded, so it’s ${frac(1, parts)}.`, key: `fs1/${parts}`,
      };
    }
    if (L === 2) {
      const d = r.pick([3, 4, 5, 10]);
      if (r.chance(0.6)) {
        const k = r.int(2, 10), n = d * k;
        return { text: `What is ${frac(1, d)} of ${n}?`, speakText: `What is one ${d === 3 ? 'third' : d === 4 ? 'quarter' : d === 5 ? 'fifth' : 'tenth'} of ${n}?`, prompt: 'Fractions', answer: String(k), choices: numChoices(k, [k + 1, k - 1, n / 2, d]), explain: `Split ${n} into ${d} equal groups: ${n} ÷ ${d} = <b>${k}</b>.`, numeric: true, key: `f1/${d}of${n}` };
      }
      const parts = r.pick([3, 4, 5, 6]), shaded = r.int(1, parts - 1);
      return {
        text: 'What fraction is shaded?', prompt: 'Fractions', visual: { kind: 'fraction', parts, shaded, shape: r.pick(['bar', 'circle']) }, answer: `${shaded}/${parts}`,
        choices: strChoices(`${shaded}/${parts}`, [`${parts - shaded}/${parts}`, `${shaded}/${parts + 1}`, `${parts}/${shaded}`, `1/${parts}`, `${shaded + 1}/${parts}`]), fracChoices: true,
        explain: `<b>${shaded}</b> of the <b>${parts}</b> equal parts are shaded: ${frac(shaded, parts)}.`, key: `fs${shaded}/${parts}`,
      };
    }
    if (r.chance(0.5)) {
      const d = r.pick([3, 4, 5]), nn = r.int(2, d - 1), k = r.int(2, 6), n = d * k;
      const ans = nn * k;
      return { text: `What is ${frac(nn, d)} of ${n}?`, speakText: `What is ${nn} ${d === 3 ? 'thirds' : d === 4 ? 'quarters' : 'fifths'} of ${n}?`, prompt: 'Fractions', answer: String(ans), choices: numChoices(ans, [k, ans + k, ans - 1, n - ans]), explain: `${frac(1, d)} of ${n} is ${k}, so ${frac(nn, d)} is ${nn} × ${k} = <b>${ans}</b>.`, numeric: true, key: `f${nn}/${d}of${n}` };
    }
    const base = r.pick([[1, 2], [1, 3], [1, 4], [3, 4], [2, 3]]);
    const m = r.int(2, 3);
    const ans = `${base[0] * m}/${base[1] * m}`;
    return {
      text: `Which fraction is the same as ${frac(base[0], base[1])}?`, prompt: 'Equal fractions', answer: ans, fracChoices: true,
      choices: strChoices(ans, [`${base[0] + m}/${base[1] + m}`, `${base[0] * m}/${base[1]}`, `${base[0]}/${base[1] * m}`, `${base[1]}/${base[0]}`]),
      explain: `Multiply the top and bottom by ${m}: ${frac(base[0], base[1])} = ${frac(base[0] * m, base[1] * m)}.`, key: `eq${ans}`,
    };
  },
  pattern(L) {
    if (L >= 4) return patternTop();
    let step, start, down = false, dbl = false;
    if (L === 1) {
      step = r.pick([1, 2, 5, 10]);
      start = step * r.int(0, 6);
    } else if (L === 2) {
      step = r.pick([3, 4, 5, 10, 2]);
      down = r.chance(0.4);
      start = step * r.int(down ? 8 : 1, down ? 14 : 8);
    } else {
      if (r.chance(0.3)) {
        dbl = true;
        start = r.pick([1, 2, 3, 5]);
      } else {
        step = r.pick([25, 50, 100, 9, 11, 6, 7, 8]);
        down = r.chance(0.3);
        start = down ? step * r.int(8, 12) : step * r.int(1, 5);
      }
    }
    const seq = [start];
    for (let i = 1; i < 5; i++) seq.push(dbl ? seq[i - 1] * 2 : seq[i - 1] + (down ? -step : step));
    const ans = seq[4];
    const shown = seq.slice(0, 4).join(', ') + ', ?';
    return {
      text: shown, prompt: 'What comes next?', speakText: `What comes next? ${seq.slice(0, 4).join(', ')}`, answer: String(ans),
      choices: numChoices(ans, dbl ? [ans + seq[3], seq[3] + 2, ans - 2] : [ans + (down ? -1 : 1), ans + (down ? step : -step) + (down ? -2 : 2), ans + (down ? -step : step), ans + 1]),
      explain: dbl ? `Each number <b>doubles</b>: ${seq[3]} × 2 = <b>${ans}</b>.` : `The numbers go ${down ? 'down' : 'up'} by <b>${step}</b> each time: ${seq[3]} ${down ? '−' : '+'} ${step} = <b>${ans}</b>.`,
      numeric: true, key: `pat${shown}`,
    };
  },
  place(L) {
    if (L >= 4) return placeTop();
    if (L <= 2) {
      const n = L === 1 ? r.int(12, 98) : r.int(102, 987);
      const s = String(n);
      const idx = r.int(0, s.length - 1);
      const digit = Number(s[idx]);
      if (digit === 0) return null;
      const value = digit * Math.pow(10, s.length - 1 - idx);
      const names = ['ones', 'tens', 'hundreds'];
      return {
        text: `What is the value of the <b class="hl">${digit}</b> in ${s.split('').map((c, i) => (i === idx ? `<span class="hl">${c}</span>` : c)).join('')}?`,
        speakText: `What is the value of the ${digit} in ${n}?`, prompt: 'Place value', answer: String(value),
        choices: numChoices(value, [digit, digit * 10, digit * 100, value + 1].filter((x) => x !== value)),
        explain: `The ${digit} is in the <b>${names[s.length - 1 - idx]}</b> place, so it’s worth <b>${value}</b>.`, numeric: true, key: `pv${n}${idx}`,
      };
    }
    const kind = r.int(0, 2);
    if (kind === 2) {
      const n = r.int(11, 99);
      if (n % 10 === 5 || n % 10 === 0) return null;
      const ans = Math.round(n / 10) * 10;
      return { text: `Round ${n} to the nearest 10.`, prompt: 'Rounding', answer: String(ans), choices: numChoices(ans, [ans - 10, ans + 10, n, Math.floor(n / 10) * 10 === ans ? ans + 10 : ans - 10]), explain: `${n} is closer to <b>${ans}</b> (ones digit ${n % 10} ${n % 10 >= 5 ? 'is 5 or more, round up' : 'is less than 5, round down'}).`, numeric: true, key: `rd${n}` };
    }
    const n = r.int(110, 890), amt = r.pick([10, 100]), more = r.chance(0.5);
    const ans = more ? n + amt : n - amt;
    return { text: `What is ${amt} ${more ? 'more' : 'less'} than ${n}?`, prompt: 'Place value', answer: String(ans), choices: numChoices(ans, [more ? n - amt : n + amt, n + (amt === 10 ? 100 : 10) * (more ? 1 : -1), n + (more ? 1 : -1), ans + 1]), explain: `Only the <b>${amt === 10 ? 'tens' : 'hundreds'}</b> digit changes: ${n} ${more ? '+' : '−'} ${amt} = <b>${ans}</b>.`, numeric: true, key: `pm${n}${amt}${more}` };
  },
  shapes(L) {
    if (L >= 3) return shapesTop();
    if (L === 1) {
      const [name, sides] = r.pick(SHAPES2D);
      return { text: `How many sides does a <b>${name}</b> have?`, prompt: 'Shapes', visual: { kind: 'shape', name, sides }, answer: String(sides), choices: numChoices(sides, [sides + 1, sides - 1, sides + 2, sides * 2]), explain: `A ${name} has <b>${sides}</b> straight sides. Count them on the picture!`, numeric: true, key: `sh${name}` };
    }
    const [q, ans, name] = r.pick(SHAPES3D);
    return { text: q, prompt: '3D shapes', visual: { kind: 'shape3d', name }, answer: String(ans), choices: numChoices(ans, [ans + 2, ans - 2, ans + 1, 4]), explain: `The answer is <b>${ans}</b>. ${name === 'cube' ? 'A cube has 6 square faces, 12 edges and 8 corners.' : ''}`, numeric: true, key: `s3${q}` };
  },
};

// ---------- top levels (Year 3/4 stretch) ----------
// Fractions with the same denominator: which is biggest or smallest, and adding them within one whole.
// Number-only modes (boss pillars) get a harder fraction of an amount instead.
function fractionTop(ctx) {
  if (ctx.numeric) {
    const d = r.pick([6, 8, 10]), nn = r.int(2, d - 1), k = r.int(3, 9), n = d * k, ans = nn * k;
    return { text: `What is ${frac(nn, d)} of ${n}?`, speakText: `What is ${fracWords(nn, d)} of ${n}?`, prompt: 'Fractions', answer: String(ans), choices: numChoices(ans, [k, ans + k, ans - k, n - ans, ans + 1]), explain: `${frac(1, d)} of ${n} is ${n} ÷ ${d} = ${k}, so ${frac(nn, d)} is ${nn} × ${k} = <b>${ans}</b>.`, numeric: true, key: `f${nn}/${d}of${n}` };
  }
  const d = r.pick([5, 6, 7, 8, 9, 10, 12]);
  if (r.chance(0.5)) {
    const tops = shuffle(Array.from({ length: d - 1 }, (_, i) => i + 1)).slice(0, 4);
    const big = r.chance(0.6), top = big ? Math.max(...tops) : Math.min(...tops), ans = `${top}/${d}`;
    return {
      text: `Which fraction is the ${big ? 'biggest' : 'smallest'}?`, speakText: `Which fraction is the ${big ? 'biggest' : 'smallest'}?`, prompt: 'Compare fractions', answer: ans, fracChoices: true,
      choices: tops.map((t) => `${t}/${d}`),
      hint: 'The bottom numbers match, so every part is the same size. Compare the top numbers!',
      explain: `Every part is one ${FRACTION_NAMES[d]}, the same size. ${big ? 'The most' : 'The fewest'} parts make the ${big ? 'biggest' : 'smallest'} fraction: ${frac(top, d)}.`,
      key: `fc${d}${big}${tops.slice().sort((a, b) => a - b).join(',')}`,
    };
  }
  const a = r.int(1, d - 2), b = r.int(1, d - 1 - a), ans = `${a + b}/${d}`;
  return {
    text: `${frac(a, d)} + ${frac(b, d)} = ?`, speakText: `What is ${fracWords(a, d)} plus ${fracWords(b, d)}?`, prompt: 'Add the fractions', answer: ans, fracChoices: true,
    choices: strChoices(ans, [`${a + b}/${d + d}`, `${a + b + 1}/${d}`, `${a + b - 1}/${d}`]),
    hint: 'The bottom numbers match. Add the top numbers and keep the bottom number the same.',
    explain: `Add the top numbers and keep the bottom one: ${a} + ${b} = ${a + b}, so ${frac(a, d)} + ${frac(b, d)} = ${frac(a + b, d)}.`,
    key: `fa${a}+${b}/${d}`,
  };
}

// Growing jumps (2, 3, 5, 8, 12, ?) and times-3 sequences.
function patternTop() {
  if (r.chance(0.5)) {
    const first = r.int(1, 3), seq = [r.int(1, 12)];
    for (let i = 0; i < 5; i++) seq.push(seq[i] + first + i);
    const ans = seq[5], shown = seq.slice(0, 5).join(', ') + ', ?';
    return {
      text: shown, prompt: 'What comes next?', speakText: `What comes next? ${seq.slice(0, 5).join(', ')}`, answer: String(ans),
      choices: numChoices(ans, [ans - 1, ans + 1, ans + first + 5, ans - 2]),
      hint: 'Look at the jumps between the numbers. Do the jumps make a pattern too?',
      explain: `The jumps grow by 1 each time: +${first}, +${first + 1}, +${first + 2}, +${first + 3}. Next is +${first + 4}: ${seq[4]} + ${first + 4} = <b>${ans}</b>.`,
      numeric: true, key: `pat${shown}`,
    };
  }
  const seq = [r.int(1, 5)];
  for (let i = 1; i < 5; i++) seq.push(seq[i - 1] * 3);
  const ans = seq[4], shown = seq.slice(0, 4).join(', ') + ', ?';
  return {
    text: shown, prompt: 'What comes next?', speakText: `What comes next? ${seq.slice(0, 4).join(', ')}`, answer: String(ans),
    choices: numChoices(ans, [seq[3] * 2, seq[3] + (seq[3] - seq[2]), ans + seq[3], ans - 3]),
    hint: 'The jumps keep getting bigger. Try multiplying instead of adding.',
    explain: `Each number is <b>3 times</b> the one before: ${seq[3]} × 3 = <b>${ans}</b>.`,
    numeric: true, key: `pat${shown}`,
  };
}

// Four-digit place value and rounding to the nearest 100 (Year 4).
function placeTop() {
  if (r.chance(0.5)) {
    const n = r.int(1023, 9876), s = String(n), idx = r.int(0, 3), digit = Number(s[idx]);
    if (digit === 0 || s.indexOf(s[idx]) !== s.lastIndexOf(s[idx])) return null;
    const value = digit * Math.pow(10, 3 - idx);
    return {
      text: `What is the value of the <b class="hl">${digit}</b> in ${s.split('').map((c, i) => (i === idx ? `<span class="hl">${c}</span>` : c)).join('')}?`,
      speakText: `What is the value of the ${digit} in ${n}?`, prompt: 'Place value', answer: String(value),
      choices: numChoices(value, [digit, digit * 10, digit * 100, digit * 1000].filter((x) => x !== value)),
      explain: `The ${digit} is in the <b>${['thousands', 'hundreds', 'tens', 'ones'][idx]}</b> place, so it’s worth <b>${value}</b>.`, numeric: true, key: `pv${n}${idx}`,
    };
  }
  const n = r.int(101, 2999);
  if (n % 100 === 0 || n % 100 === 50) return null;
  const ans = Math.round(n / 100) * 100, lo = Math.floor(n / 100) * 100, tens = Math.floor(n / 10) % 10;
  return {
    text: `Round ${n} to the nearest 100.`, prompt: 'Rounding', answer: String(ans),
    choices: numChoices(ans, [ans - 100, ans + 100, Math.round(n / 10) * 10, lo === ans ? lo + 100 : lo]),
    hint: 'Which two hundreds is it between? The tens digit decides.',
    explain: `${n} is between ${lo} and ${lo + 100}. The tens digit is ${tens}, so round ${tens >= 5 ? 'up' : 'down'} to <b>${ans}</b>.`,
    numeric: true, key: `rh${n}`,
  };
}

// Right angles in shapes and turns, or faces, edges and vertices of prisms and pyramids (hidden edges dashed).
function shapesTop() {
  if (r.chance(0.5)) {
    const [text, ans, visual, explain] = r.pick(RIGHT_ANGLES);
    return { text, speakText: plain(text), prompt: 'Right angles', ...(visual ? { visual } : {}), answer: String(ans), choices: numChoices(ans, [ans + 1, ans - 1, ans + 2, 4, 2, 1, 0]), hint: 'A right angle is a square corner, like the corner of a book. A quarter turn makes one.', explain, numeric: true, key: `ra${text}` };
  }
  const [text, ans, name, explain] = r.pick(SHAPES3D_TOP);
  return { text, speakText: text, prompt: '3D shapes', visual: { kind: 'shape3d', name, hidden: true }, answer: String(ans), choices: numChoices(ans, [...SOLIDS[name], ans + 1, ans - 1, ans + 2]), hint: 'Dashed lines show the edges hiding at the back. Count the front and the back!', explain, numeric: true, key: `s3${text}` };
}
